import { createClient } from "@supabase/supabase-js";

const DEV_TOKEN_KEY = "kuro_dev_token";

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!,
);

let cachedToken: string | null = import.meta.env.DEV
  ? localStorage.getItem(DEV_TOKEN_KEY)
  : null;

supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.access_token) {
    cachedToken = session.access_token;
    localStorage.removeItem(DEV_TOKEN_KEY);
  }
  // When session is null, leave cachedToken alone - dev token may still be active.
  // Explicit sign-out (signOut()) sets cachedToken = null directly.
});

export function setDevToken(token: string | null) {
  cachedToken = token;
  if (token) localStorage.setItem(DEV_TOKEN_KEY, token);
  else localStorage.removeItem(DEV_TOKEN_KEY);
}

export async function signInWithDiscord() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: "discord",
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      // `connections` lets the server read the member's linked Xbox-Discord
      // account at login to auto-fill their gamertag (see fetchXboxGamertag).
      scopes: "identify guilds.members.read guilds.join connections",
    },
  });
  if (error) throw error;
}

// An OAuth round-trip always lands on /auth/callback, which then SPA-navigates
// onward (the dashboard by default). A caller that re-runs login for a specific
// reason - e.g. the "Import from Xbox" button, which re-auths only to obtain a
// fresh provider token - can stash where to return to here so the user lands
// back where they started instead of on the dashboard. Session-scoped: it's a
// single in-flight redirect, not durable state.
const POST_AUTH_REDIRECT_KEY = "kuro_post_auth_redirect";

export function setPostAuthRedirect(path: string) {
  sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, path);
}

export function takePostAuthRedirect(): string | null {
  const path = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
  sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  return path;
}

// `async` is here for parity
export async function signOut() {
  setDevToken(null);

  cachedToken = null;
  // supabase.auth.signOut() can deadlock on its internal auth lock indefinitely
  // - even with scope: "local" (https://github.com/supabase/auth-js/issues/762).
  // Manually clear the persisted session so a subsequent page load doesn't pick
  // it back up, then fire-and-forget the supabase call for in-memory cleanup.
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith("sb-")) localStorage.removeItem(key);
  }

  void supabase.auth.signOut().catch(() => {});
}

export async function getAccessToken(): Promise<string | null> {
  if (cachedToken) return cachedToken;
  const { data } = await supabase.auth.getSession();
  cachedToken = data.session?.access_token ?? null;
  return cachedToken;
}
